import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Target, Layers as LayersIcon, Zap as ZapIcon, Award } from "lucide-react";
import { SERVICES } from "@/lib/data";
import { useSEO } from "@/hooks/useSEO";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useSEO({
    title: service ? `${service.title} | EnPrex Services` : "Service | EnPrex Services",
    description: service?.short || "Specialist consulting for energy and commodity trading technology.",
    path: `/services/${slug || ""}`,
  });

  if (!service) {
    return (
      <div className="container-x py-32 text-center" data-testid="service-not-found">
        <h1 className="font-display text-4xl text-white">Service not found</h1>
        <Link to="/services" className="mt-6 inline-block text-[#D4AF37] underline">← Back to services</Link>
      </div>
    );
  }

  const Icon = service.icon;

  return (
    <article data-testid="service-detail" className="pb-24">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A]/70 to-[#050A14]" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#2563EB]/10 blur-[140px]" />
        <div className="container-x relative pt-16 pb-20">
          <Link to="/services" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#D4AF37] font-mono mb-10" data-testid="back-to-services">
            <ArrowLeft size={14} /> All Services
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Icon className="text-[#D4AF37] mb-8" size={44} strokeWidth={1.2} />
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4 font-mono">— Service</div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light text-white tracking-tighter leading-[0.98] max-w-5xl">
              {service.title}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-3xl font-light leading-relaxed">
              {service.short}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="container-x py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono">01 — Overview</div>
        </div>
        <div className="lg:col-span-8">
          <p className="text-xl md:text-2xl text-white font-light leading-relaxed tracking-tight">
            {service.overview}
          </p>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-white/5">
        <div className="container-x py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">02 — Business Challenges</div>
            <h2 className="font-display text-3xl md:text-4xl text-white font-light tracking-tight leading-tight">
              What we typically see.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {service.challenges.map((c, i) => (
              <div key={i} className="flex items-start gap-4 border-b border-white/5 pb-4">
                <span className="font-mono text-xs text-slate-500 mt-1 min-w-[24px]">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-slate-300 leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="border-t border-white/5 bg-[#0B1F3A]/30">
        <div className="container-x py-20">
          <div className="grid lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">03 — Our Approach</div>
              <h2 className="font-display text-3xl md:text-4xl text-white font-light tracking-tight leading-tight">
                A disciplined four-phase engagement.
              </h2>
            </div>
            <div className="lg:col-span-8 text-slate-400 font-light leading-relaxed">
              Every engagement — whether a two-week assessment or a multi-year program — follows the same disciplined arc. It is what allows us to be predictable at senior levels of ambition.
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
            {service.approach.map((step, i) => (
              <div key={step.title} className="bg-[#050A14] p-8">
                <div className="font-display text-5xl font-light text-[#D4AF37]">0{i + 1}</div>
                <h3 className="font-display text-xl text-white mt-4 font-medium">{step.title}</h3>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes + Deliverables */}
      <section className="container-x py-20 border-t border-white/5 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">04 — Business Outcomes</div>
          <h2 className="font-display text-3xl md:text-4xl text-white font-light tracking-tight leading-tight mb-8">
            What clients realise.
          </h2>
          <ul className="space-y-4">
            {service.outcomes.map((o, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="text-[#D4AF37] mt-1 flex-shrink-0" size={18} />
                <span className="text-slate-300 leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">05 — Key Deliverables</div>
          <h2 className="font-display text-3xl md:text-4xl text-white font-light tracking-tight leading-tight mb-8">
            What we hand over.
          </h2>
          <ul className="space-y-4">
            {service.deliverables.map((d, i) => (
              <li key={i} className="flex items-start gap-3">
                <LayersIcon className="text-[#D4AF37] mt-1 flex-shrink-0" size={18} strokeWidth={1.4} />
                <span className="text-slate-300 leading-relaxed">{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Value */}
      <section className="border-t border-white/5 bg-gradient-to-br from-[#0B1F3A] to-[#050A14]">
        <div className="container-x py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Award className="text-[#D4AF37] mb-6" size={40} strokeWidth={1.2} />
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">06 — Value Delivered</div>
          </div>
          <div className="lg:col-span-8">
            <p className="font-display text-2xl md:text-3xl text-white font-light leading-relaxed tracking-tight italic">
              "{service.value}"
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-20">
        <div className="border border-[#D4AF37]/25 bg-white/[0.02] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-2xl md:text-4xl font-light text-white tracking-tight leading-tight max-w-2xl">
              Explore what this engagement could look like for your business.
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl">
              All enquiries are managed exclusively through our secure enquiry form. A senior consultant will respond within one business day.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/contact" data-testid="service-cta-consultation" className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#FDE047] transition-colors whitespace-nowrap">
              Book a Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="service-cta-proposal" className="inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors whitespace-nowrap">
              Request a Proposal <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
